import {
  ATTACHMENT_ACCEPT,
  DEFAULT_FILE_MAX_BYTES,
  formatBytes,
  MAX_ATTACHMENTS_PER_MESSAGE,
  type EndpointDto,
} from '@agent-platform/shared';
import { Link } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CopyField } from '@/components/copy-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/browser';
import { cn } from '@/lib/utils';
import type { ExpectedHeader } from './agent-headers';

/** `x-api-access-token` → `apiAccessToken`: a variable name for a header value. */
function varName(header: string): string {
  const words = header
    .replace(/^x-/, '')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);
  return (
    words.map((w, i) => (i === 0 ? w.toLowerCase() : w[0]!.toUpperCase() + w.slice(1))).join('') ||
    'value'
  );
}

/** `x-store-id` → `X_STORE_ID`: an environment variable for a header value. */
const envName = (header: string) => header.toUpperCase().replace(/[^A-Z0-9]+/g, '_');

interface Snippet {
  id: string;
  label: string;
  files: Array<{ name: string; code: string }>;
}

function buildSnippets(url: string, headers: ExpectedHeader[], stateful: boolean): Snippet[] {
  const thread = stateful ? ', "threadId": "conversation-1"' : '';
  const curl = [
    `curl -X POST '${url}' \\`,
    `  -H "Authorization: Bearer $AGENT_API_KEY" \\`,
    `  -H 'Content-Type: application/json' \\`,
    ...headers.map((h) => `  -H '${h.key}: <${h.key}>' \\`),
    `  -d '{"input": "Hello!"${thread}}'`,
  ].join('\n');

  const headerLines = (indent: string, source: 'var' | 'env') =>
    headers.map(
      (h) =>
        `${indent}'${h.key}': ${source === 'var' ? varName(h.key) : `process.env.${envName(h.key)}`},`,
    );
  const params = headers.map((h) => varName(h.key));

  const fetchJson = [
    `// Call from your server: the API key must never reach a browser.`,
    `const AGENT_URL = '${url}';`,
    ``,
    `export async function askAgent(input${stateful ? ', threadId' : ''}${params.length ? `, { ${params.join(', ')} }` : ''}) {`,
    `  const response = await fetch(AGENT_URL, {`,
    `    method: 'POST',`,
    `    headers: {`,
    '      Authorization: `Bearer ${process.env.AGENT_API_KEY}`,',
    `      'Content-Type': 'application/json',`,
    ...headerLines('      ', 'var'),
    `    },`,
    stateful
      ? `    // Send the same threadId with every message of one conversation.\n    body: JSON.stringify({ input, threadId }),`
      : `    body: JSON.stringify({ input }),`,
    `  });`,
    '  if (!response.ok) throw new Error(`Agent call failed: ${response.status} ${await response.text()}`);',
    `  const { output, usage } = await response.json();`,
    `  console.debug('tokens', usage.inputTokens, usage.outputTokens);`,
    `  return output.text;`,
    `}`,
  ].join('\n');

  const parser = [
    `/** Reads the agent's Server-Sent Events: calls onText for each piece of the reply. */`,
    `export async function readAgentStream(response, onText) {`,
    `  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();`,
    `  let buffer = '';`,
    `  for (;;) {`,
    `    const { value, done } = await reader.read();`,
    `    if (done) return;`,
    `    buffer += value;`,
    `    let end;`,
    `    while ((end = buffer.indexOf('\\n\\n')) !== -1) {`,
    `      const frame = buffer.slice(0, end);`,
    `      buffer = buffer.slice(end + 2);`,
    `      const data = frame.split('\\n').find((line) => line.startsWith('data: '));`,
    `      if (!data) continue;`,
    `      const event = JSON.parse(data.slice(6));`,
    `      if (event.type === 'text.delta') onText(event.text);`,
    `      if (event.type === 'tool.call') console.debug('tool', event.name, event.args);`,
    `      if (event.type === 'run.error') throw new Error(event.message);`,
    `      if (event.type === 'run.completed') return event; // { output, usage, latencyMs }`,
    `    }`,
    `  }`,
    `}`,
  ].join('\n');

  const fetchStream = [
    `const AGENT_URL = '${url}';`,
    ``,
    `const response = await fetch(AGENT_URL, {`,
    `  method: 'POST',`,
    `  headers: {`,
    '    Authorization: `Bearer ${process.env.AGENT_API_KEY}`,',
    `    'Content-Type': 'application/json',`,
    `    Accept: 'text/event-stream',`,
    ...headerLines('    ', 'env'),
    `  },`,
    `  body: JSON.stringify({ input: 'Hello!', stream: true${stateful ? ", threadId: 'conversation-1'" : ''} }),`,
    `});`,
    'if (!response.ok) throw new Error(`Agent call failed: ${response.status}`);',
    `await readAgentStream(response, (text) => process.stdout.write(text));`,
    ``,
    parser,
  ].join('\n');

  const filesCurl = [
    `# 1. Upload the file: the body is the file itself.`,
    `#    Reply: { "fileId", "name", "mediaType", "sizeBytes" }`,
    `FILE_ID=$(curl -s -X POST '${url}/files?name=photo.png' \\`,
    `  -H "Authorization: Bearer $AGENT_API_KEY" \\`,
    `  -H 'Content-Type: image/png' \\`,
    `  --data-binary @photo.png | jq -r .fileId)`,
    ``,
    `# 2. Send it with a message (up to ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message).`,
    `curl -X POST '${url}' \\`,
    `  -H "Authorization: Bearer $AGENT_API_KEY" \\`,
    `  -H 'Content-Type: application/json' \\`,
    ...headers.map((h) => `  -H '${h.key}: <${h.key}>' \\`),
    `  -d '{"input": [{"role": "user", "content": "What is in this picture?", "attachments": [{"fileId": "'"$FILE_ID"'"}]}]${thread}}'`,
    ``,
    `# Or skip the upload and send a small file inline (base64; it must fit the request):`,
    `#   "attachments": [{"name": "notes.md", "mediaType": "text/markdown", "data": "<base64>"}]`,
  ].join('\n');

  const filesFetch = [
    `// Call from your server: the API key must never reach a browser.`,
    `import { readFile } from 'node:fs/promises';`,
    `import { basename } from 'node:path';`,
    ``,
    `const AGENT_URL = '${url}';`,
    'const auth = { Authorization: `Bearer ${process.env.AGENT_API_KEY}` };',
    ``,
    `/** Uploads a file once and returns its fileId; reuse the id in later messages. */`,
    `export async function uploadFile(path, mediaType) {`,
    '  const response = await fetch(`${AGENT_URL}/files?name=${encodeURIComponent(basename(path))}`, {',
    `    method: 'POST',`,
    `    headers: { ...auth, 'Content-Type': mediaType },`,
    `    body: await readFile(path),`,
    `  });`,
    '  if (!response.ok) throw new Error(`Upload failed: ${response.status} ${await response.text()}`);',
    `  const { fileId } = await response.json();`,
    `  return fileId;`,
    `}`,
    ``,
    `/** Sends a message with attachments: [{ fileId }] or inline [{ name, mediaType, data }]. */`,
    `export async function askWithFiles(content, attachments${stateful ? ', threadId' : ''}) {`,
    `  const response = await fetch(AGENT_URL, {`,
    `    method: 'POST',`,
    `    headers: {`,
    `      ...auth,`,
    `      'Content-Type': 'application/json',`,
    ...headerLines('      ', 'env'),
    `    },`,
    `    body: JSON.stringify({`,
    `      input: [{ role: 'user', content, attachments }],`,
    ...(stateful ? [`      threadId,`] : []),
    `    }),`,
    `  });`,
    '  if (!response.ok) throw new Error(`Agent call failed: ${response.status} ${await response.text()}`);',
    `  const { output } = await response.json();`,
    `  return output.text;`,
    `}`,
    ``,
    `// An uploaded image…`,
    `const fileId = await uploadFile('./photo.png', 'image/png');`,
    `console.log(await askWithFiles('What is in this picture?', [{ fileId }]${stateful ? ", 'conversation-1'" : ''}));`,
    ``,
    `// …or a small file inline (base64; a data: URL works too).`,
    `const notes = await readFile('./notes.md');`,
    `console.log(`,
    `  await askWithFiles('Summarise these notes', [`,
    `    { name: 'notes.md', mediaType: 'text/markdown', data: notes.toString('base64') },`,
    `  ]${stateful ? ", 'conversation-1'" : ''}),`,
    `);`,
  ].join('\n');

  const server = [
    `// server.mjs: run with \`AGENT_API_KEY=... node server.mjs\`, then open http://localhost:8787`,
    `// The browser talks to this server; only the server holds the API key.`,
    `import { createServer } from 'node:http';`,
    `import { readFile } from 'node:fs/promises';`,
    ``,
    `const AGENT_URL = '${url}';`,
    ``,
    `createServer(async (req, res) => {`,
    `  if (req.method === 'GET' && req.url === '/') {`,
    `    res.writeHead(200, { 'content-type': 'text/html; charset=utf-8' });`,
    `    return res.end(await readFile(new URL('./index.html', import.meta.url)));`,
    `  }`,
    `  if (req.method === 'POST' && req.url === '/chat') {`,
    `    let body = '';`,
    `    for await (const chunk of req) {`,
    `      body += chunk;`,
    `      // Attachments arrive inline (base64): cap the request size.`,
    `      if (body.length > 20_000_000) return res.writeHead(413).end('Too large');`,
    `    }`,
    `    const { input, threadId, attachments = [] } = JSON.parse(body);`,
    `    const upstream = await fetch(AGENT_URL, {`,
    `      method: 'POST',`,
    `      headers: {`,
    '        Authorization: `Bearer ${process.env.AGENT_API_KEY}`,',
    `        'Content-Type': 'application/json',`,
    `        Accept: 'text/event-stream',`,
    ...(headers.length
      ? [`        // In a real app these come from the signed-in user's session.`]
      : []),
    ...headerLines('        ', 'env'),
    `      },`,
    `      body: JSON.stringify({`,
    `        input: [{ role: 'user', content: input, attachments }],`,
    `        threadId,`,
    `        stream: true,`,
    `      }),`,
    `    });`,
    `    res.writeHead(upstream.status, {`,
    `      'content-type': upstream.headers.get('content-type') ?? 'text/plain',`,
    `    });`,
    `    for await (const chunk of upstream.body) res.write(chunk);`,
    `    return res.end();`,
    `  }`,
    `  res.writeHead(404).end();`,
    `}).listen(8787, () => console.log('Chat on http://localhost:8787'));`,
  ].join('\n');

  const html = [
    `<!doctype html>`,
    `<html lang="en">`,
    `<head>`,
    `  <meta charset="utf-8" />`,
    `  <meta name="viewport" content="width=device-width, initial-scale=1" />`,
    `  <title>Chat</title>`,
    `  <style>`,
    `    body { font: 15px/1.5 system-ui, sans-serif; max-width: 42rem; margin: 2rem auto; padding: 0 1rem; }`,
    `    #log { display: grid; gap: .75rem; margin-bottom: 1rem; }`,
    `    .user { justify-self: end; background: #f1f1f1; border-radius: 1rem; padding: .5rem .9rem; white-space: pre-wrap; }`,
    `    .agent { white-space: pre-wrap; }`,
    `    form { display: flex; gap: .5rem; align-items: center; }`,
    `    .files { font-size: 13px; color: #666; }`,
    `    label.attach { cursor: pointer; padding: .4rem .6rem; border-radius: 999px; }`,
    `    label.attach:hover { background: #f1f1f1; }`,
    `    input { flex: 1; padding: .6rem .9rem; border: 1px solid #ccc; border-radius: 999px; }`,
    `    button { padding: .6rem 1.1rem; border: 0; border-radius: 999px; background: #111; color: #fff; }`,
    `  </style>`,
    `</head>`,
    `<body>`,
    `  <div id="log" aria-live="polite"></div>`,
    `  <div id="picked" class="files"></div>`,
    `  <form id="form">`,
    `    <label class="attach" title="Attach images or files">📎<input id="files" type="file" multiple hidden accept="${ATTACHMENT_ACCEPT}" /></label>`,
    `    <input id="input" placeholder="Message" autocomplete="off" aria-label="Message" />`,
    `    <button>Send</button>`,
    `  </form>`,
    `  <script type="module">`,
    `    // One conversation per browser: the agent remembers it by threadId.`,
    `    const threadId = localStorage.threadId ??= crypto.randomUUID();`,
    `    const log = document.getElementById('log');`,
    `    const add = (cls, text) => log.appendChild(Object.assign(document.createElement('div'), { className: cls, textContent: text }));`,
    `    const picker = document.getElementById('files');`,
    `    const picked = document.getElementById('picked');`,
    "    picker.addEventListener('change', () => (picked.textContent = [...picker.files].map((f) => `📎 ${f.name}`).join('  ')));",
    `    // Files go inline as data: URLs (fine for a few MB; upload bigger ones to /files first).`,
    `    const toAttachment = (file) => new Promise((resolve, reject) => {`,
    `      const reader = new FileReader();`,
    `      reader.onload = () => resolve({ name: file.name, mediaType: file.type || 'text/plain', data: reader.result });`,
    `      reader.onerror = () => reject(reader.error);`,
    `      reader.readAsDataURL(file);`,
    `    });`,
    ``,
    `    document.getElementById('form').addEventListener('submit', async (e) => {`,
    `      e.preventDefault();`,
    `      const input = document.getElementById('input');`,
    `      const text = input.value.trim();`,
    `      const files = [...picker.files];`,
    `      if (!text && !files.length) return;`,
    `      const attachments = await Promise.all(files.map(toAttachment));`,
    `      input.value = '';`,
    `      picker.value = '';`,
    `      picked.textContent = '';`,
    "      add('user', [text, ...files.map((f) => `📎 ${f.name}`)].filter(Boolean).join('\\n'));",
    `      const reply = add('agent', '…');`,
    `      const response = await fetch('/chat', {`,
    `        method: 'POST',`,
    `        headers: { 'Content-Type': 'application/json' },`,
    `        body: JSON.stringify({ input: text, threadId, attachments }),`,
    `      });`,
    `      reply.textContent = '';`,
    `      try {`,
    `        await readAgentStream(response, (piece) => (reply.textContent += piece));`,
    `      } catch (error) {`,
    '        reply.textContent = `Something went wrong: ${error.message}`;',
    `      }`,
    `    });`,
    ``,
    ...parser.split('\n').map((l) => `    ${l.replace(/^export /, '')}`),
    `  </script>`,
    `</body>`,
    `</html>`,
  ].join('\n');

  return [
    { id: 'curl', label: 'cURL', files: [{ name: 'Terminal', code: curl }] },
    { id: 'fetch', label: 'fetch', files: [{ name: 'agent.js', code: fetchJson }] },
    { id: 'stream', label: 'Streaming', files: [{ name: 'stream.js', code: fetchStream }] },
    {
      id: 'files',
      label: 'Images & files',
      files: [
        { name: 'Terminal', code: filesCurl },
        { name: 'files.js', code: filesFetch },
      ],
    },
    {
      id: 'chat',
      label: 'Chat screen',
      files: [
        { name: 'server.mjs', code: server },
        { name: 'index.html', code: html },
      ],
    },
  ];
}

function CodeBlock({ name, code }: { name: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await copyText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed. Select the code and copy it manually.');
    }
  };
  return (
    <figure className="bg-card shadow-panel overflow-hidden rounded-xl">
      <figcaption className="flex h-10 items-center justify-between border-b pr-1.5 pl-4">
        <span className="text-muted-foreground font-mono text-xs">{name}</span>
        <Button variant="ghost" size="xs" onClick={copy} aria-label={`Copy ${name}`}>
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </figcaption>
      <pre className="max-h-[32rem] overflow-auto p-4 font-mono text-xs leading-5">
        <code>{code}</code>
      </pre>
    </figure>
  );
}

/** The ready-to-run examples for an endpoint (shared by the agent's Code tab and the endpoint page). */
export function EndpointExamples({
  endpoint,
  expected,
  stateful,
}: {
  endpoint: EndpointDto;
  expected: ExpectedHeader[];
  stateful: boolean;
}) {
  const [tab, setTab] = useState('fetch');
  const snippets = buildSnippets(endpoint.url, expected, stateful);
  const current = snippets.find((s) => s.id === tab) ?? snippets[0]!;
  return (
    <section className="grid gap-3">
      <div role="tablist" aria-label="Examples" className="flex flex-wrap gap-1">
        {snippets.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === current.id}
            onClick={() => setTab(s.id)}
            className={cn(
              'text-muted-foreground hover:text-foreground aria-selected:bg-accent aria-selected:text-foreground focus-visible:ring-ring/50 h-[30px] rounded-full px-3 text-sm font-medium outline-none focus-visible:ring-3',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
      {current.id === 'files' && !endpoint.attachmentsEnabled && (
        <p className="text-muted-foreground rounded-lg border border-dashed p-3 text-xs">
          Attachments are turned off for this endpoint: these calls answer 400 until you turn on
          “Allow file and image attachments” in the endpoint settings.
        </p>
      )}
      {current.files.map((f) => (
        <CodeBlock key={f.name} name={f.name} code={f.code} />
      ))}
    </section>
  );
}

/** How to call this agent from code: its endpoint, auth, headers and ready-to-run examples. */
export function AgentCode({
  endpoint,
  expected,
  stateful,
  published,
  creating,
  onCreateEndpoint,
}: {
  endpoint: EndpointDto | undefined;
  expected: ExpectedHeader[];
  stateful: boolean;
  published: boolean;
  creating: boolean;
  onCreateEndpoint: () => void;
}) {
  if (!endpoint) {
    return (
      <div className="mx-auto grid max-w-xl justify-items-center gap-3 py-16 text-center">
        <h2 className="text-sm font-medium">No endpoint yet</h2>
        <p className="text-muted-foreground text-xs">
          An endpoint gives this agent a URL your app can call with an API key.
          {!published && ' Publish the agent first: endpoints serve the live version.'}
        </p>
        <Button size="sm" onClick={onCreateEndpoint} disabled={creating || !published}>
          {creating ? 'Creating…' : 'Create endpoint'}
        </Button>
      </div>
    );
  }
  return (
    <div className="mx-auto grid w-full max-w-4xl gap-6">
      <section className="grid gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-medium">Endpoint</h2>
          <Badge variant={endpoint.enabled ? 'secondary' : 'outline'}>
            {endpoint.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          {endpoint.streamingEnabled && <Badge variant="outline">Streaming</Badge>}
          {endpoint.attachmentsEnabled && <Badge variant="outline">Attachments</Badge>}
          <Button variant="link" size="sm" className="ml-auto" asChild>
            <Link to="/endpoints/$endpointId" params={{ endpointId: endpoint.id }}>
              Mapping and settings
            </Link>
          </Button>
        </div>
        <CopyField value={endpoint.url} label="Endpoint URL" />
        <ul className="text-muted-foreground grid gap-1 text-xs">
          <li>
            Authenticate with an API key as <code>Authorization: Bearer …</code> or{' '}
            <code>x-api-key</code>.{' '}
            <Link to="/api-keys" className="underline">
              Create an API key
            </Link>
            .
          </li>
          <li>
            Body: <code>input</code> (a string, or <code>{'[{ role, content, attachments }]'}</code>
            )
            {stateful && (
              <>
                , <code>threadId</code> to continue a conversation
              </>
            )}
            , and <code>stream: true</code> for Server-Sent Events.
          </li>
          <li>
            {!endpoint.attachmentsEnabled && (
              <>
                <strong className="text-foreground">Attachments are turned off</strong> for this
                endpoint (turn them on in Mapping and settings).{' '}
              </>
            )}
            Attachments: upload with <code>POST …/files?name=photo.png</code> (the body is the file)
            and send <code>{'{ fileId }'}</code>, or send it inline as{' '}
            <code>{'{ name, mediaType, data }'}</code> (base64). Up to {MAX_ATTACHMENTS_PER_MESSAGE}{' '}
            per message, each up to {formatBytes(DEFAULT_FILE_MAX_BYTES)} by default: images (PNG,
            JPEG, GIF, WebP), PDF, text, Markdown, CSV and JSON.
          </li>
          <li>
            Reply: <code>{'{ runId, output: { text }, usage, threadId }'}</code>. Clients always get
            the published version.
          </li>
          {expected.length > 0 && (
            <li>
              Headers the mapping reads:{' '}
              {expected.map((h, i) => (
                <span key={h.key}>
                  {i > 0 && ', '}
                  <code>{h.key}</code>
                  {h.required && ' (required)'}
                </span>
              ))}
              .
            </li>
          )}
        </ul>
      </section>

      <EndpointExamples endpoint={endpoint} expected={expected} stateful={stateful} />
    </div>
  );
}
